import { useNavigate } from "@solidjs/router";
import { urls } from "@storytell/core";
import { TbLoader2 } from "solid-icons/tb";
import { type Component, createUniqueId, For, Show } from "solid-js";
import { useI18n } from "~/i18n/context";
import { trackFeature } from "~/lib/trackFeature";
import { useWire } from "~/wire";
import { resolveRunningChatLabel } from "../runningChatLabel";
import type { RunningChatEntry } from "../runningMerge";
import { getRunningChatPreview, useRunningChats } from "../useRunningChats";

export const RunningChatsSection: Component<{ variant?: "panel" | "page"; onNavigate?: () => void }> = (props) => {
  const wire = useWire();
  const navigate = useNavigate();
  const { t } = useI18n();
  const running = useRunningChats();
  const headingId = createUniqueId();

  // The live title store wins (it updates the moment generation lands), then
  // the server snapshot's title; for still-untitled chats, this tab's captured
  // prompt preview, then the snapshot's (which survives refreshes).
  const labelFor = (entry: RunningChatEntry): string =>
    resolveRunningChatLabel({
      title: wire.chatTitle.getChatTitle(entry.chatId) || entry.title,
      preview: getRunningChatPreview(entry.chatId) ?? entry.preview,
      generic: t("notifications.running.generic_label"),
    });

  // Server entries carry their own project; local-only entries fall back to the
  // session's current project, then the flat url.
  const openChat = (entry: RunningChatEntry) => {
    const projectId = entry.projectId ?? wire.currentProjectId();
    trackFeature({ feature: "notifications", action: "running_chat_open", via: "button" });
    props.onNavigate?.();
    navigate(urls.chatHref(projectId, entry.chatId));
  };

  const isPage = () => props.variant === "page";

  return (
    <Show when={running().length > 0}>
      <section aria-labelledby={headingId} class={isPage() ? "mb-6" : "border-b border-primary-border bg-panel"}>
        <h2
          id={headingId}
          class={
            isPage()
              ? "mb-2 text-xs font-semibold tracking-wide text-muted-text uppercase"
              : "px-4 pt-3 pb-1 text-xs font-semibold tracking-wide text-muted-text uppercase"
          }
        >
          {t("notifications.running.title")}
        </h2>
        <ul
          class={
            isPage()
              ? "divide-y divide-primary-border overflow-hidden rounded-lg border border-primary-border bg-panel"
              : "pb-1"
          }
        >
          <For each={running()}>
            {(entry) => (
              <li>
                <button
                  type="button"
                  onClick={() => openChat(entry)}
                  class="flex w-full min-w-0 items-center gap-3 px-4 py-2 text-left transition-colors hover:bg-panel-hover focus:outline-none focus-visible:bg-panel-hover"
                >
                  <TbLoader2
                    class="size-4 shrink-0 animate-spin text-accent motion-reduce:animate-none"
                    aria-hidden="true"
                  />
                  <span class="min-w-0 flex-1 truncate text-sm text-primary-text">{labelFor(entry)}</span>
                </button>
              </li>
            )}
          </For>
        </ul>
      </section>
    </Show>
  );
};
